// src/components/Sections/Admin/Roles/MatrizPermisos.jsx
//
// Un nivel por módulo —Sin acceso · Ver · Editar— y, si edita, si sus cambios pasan
// por aprobación.
//
// Antes eran tres casillas por fila (ver, editar, requiere aprobación) y las reglas de
// coherencia se aplicaban por detrás: marcar «editar» marcaba «ver», etc. Con un nivel
// la regla se ve en la interfaz misma y no hay combinaciones sin sentido que dibujar.
// Las reglas siguen pasando por `aplicarReglas`, que es la misma lógica del servidor.
import React from 'react';
import { Switch, Tooltip, Empty } from 'antd';
import { FiEyeOff, FiEye, FiEdit3, FiInfo } from 'react-icons/fi';
import { aplicarReglas } from '../../../../config/rbac';

const NIVELES = [
  { value: 'ninguno', label: 'Sin acceso', icon: <FiEyeOff size={13} />, ayuda: 'El módulo no aparece en el menú.' },
  { value: 'ver', label: 'Ver', icon: <FiEye size={13} />, ayuda: 'Entra al módulo y consulta sus datos.' },
  { value: 'editar', label: 'Editar', icon: <FiEdit3 size={13} />, ayuda: 'Crea, modifica y elimina dentro del módulo. Implica ver.' },
];

export const nivelDe = p => (p?.editar ? 'editar' : p?.ver ? 'ver' : 'ninguno');

// Pasar de un nivel a otro con las mismas reglas que el servidor.
const conNivel = (permisos, modulo, nivel) => {
  if (nivel === 'ninguno') return aplicarReglas(permisos, modulo, 'ver', false);
  if (nivel === 'ver') return aplicarReglas(aplicarReglas(permisos, modulo, 'editar', false), modulo, 'ver', true);
  return aplicarReglas(permisos, modulo, 'editar', true);
};

const Segmentos = ({ valor, onChange, disabled, label }) => (
  <div className="inline-flex p-1 rounded-full bg-gray-100" role="radiogroup" aria-label={label}>
    {NIVELES.map(n => {
      const activo = valor === n.value;
      return (
        <Tooltip key={n.value} title={n.ayuda} mouseEnterDelay={0.4}>
          <button type="button" role="radio" aria-checked={activo} disabled={disabled}
            onClick={() => !activo && onChange(n.value)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-colors disabled:cursor-default
              ${activo
                ? n.value === 'editar' ? 'bg-primary text-white shadow-sm'
                  : n.value === 'ver' ? 'bg-white text-primary shadow-sm'
                    : 'bg-white text-gray-600 shadow-sm'
                : 'text-gray-500 hover:text-gray-800 disabled:hover:text-gray-500'}`}>
            {n.icon} {n.label}
          </button>
        </Tooltip>
      );
    })}
  </div>
);

const MatrizPermisos = ({ modulos = [], permisos = {}, onChange, soloLectura = false }) => {
  if (!modulos.length) {
    return <Empty description="No se pudo cargar el catálogo de módulos" className="py-8" />;
  }

  const cambiarNivel = (modulo, nivel) => !soloLectura && onChange(conNivel(permisos, modulo, nivel));
  const cambiarAprobacion = (modulo, valor) =>
    !soloLectura && onChange(aplicarReglas(permisos, modulo, 'requiereAprobacion', valor));
  const aplicarATodos = nivel =>
    !soloLectura && onChange(modulos.reduce((acc, m) => conNivel(acc, m.key, nivel), permisos));

  const niveles = modulos.map(m => nivelDe(permisos?.[m.key]));
  const todosEn = nivel => niveles.every(n => n === nivel);

  return (
    <div>
      {!soloLectura && (
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 mb-2 rounded-2xl bg-gray-50">
          <span className="text-xs font-bold uppercase tracking-wide text-gray-500">Aplicar a todos los módulos</span>
          <div className="flex flex-wrap gap-1.5">
            {NIVELES.map(n => (
              <button key={n.value} type="button" onClick={() => aplicarATodos(n.value)} disabled={todosEn(n.value)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-gray-200 bg-white text-xs font-semibold text-gray-700 hover:border-primary hover:text-primary transition-colors disabled:opacity-40 disabled:pointer-events-none">
                {n.icon} {n.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <ul className="m-0 p-0 list-none divide-y divide-gray-100">
        {modulos.map(m => {
          const p = permisos?.[m.key] || {};
          const nivel = nivelDe(p);
          return (
            <li key={m.key} className={`flex flex-col md:flex-row md:items-center gap-3 px-4 py-3.5 transition-colors
              ${nivel === 'ninguno' ? '' : 'bg-blue-50/40'}`}>
              <div className="min-w-0 flex-1">
                <p className={`text-sm font-bold m-0 ${nivel === 'ninguno' ? 'text-gray-500' : 'text-gray-900'}`}>{m.label}</p>
                {m.descripcion && <p className="text-xs text-gray-500 m-0 mt-0.5">{m.descripcion}</p>}
              </div>

              <div className="flex flex-wrap items-center gap-3 md:justify-end">
                {soloLectura ? (
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold
                    ${nivel === 'editar' ? 'bg-primary text-white' : nivel === 'ver' ? 'bg-blue-50 text-primary' : 'bg-gray-100 text-gray-500'}`}>
                    {NIVELES.find(n => n.value === nivel).icon} {NIVELES.find(n => n.value === nivel).label}
                  </span>
                ) : (
                  <Segmentos valor={nivel} onChange={v => cambiarNivel(m.key, v)} label={`Nivel de acceso a ${m.label}`} />
                )}

                {/* «Requiere aprobación» es un matiz de CÓMO se edita: solo existe con Editar. */}
                <span className={`inline-flex items-center gap-2 w-[178px] ${nivel === 'editar' ? '' : 'invisible'}`}>
                  <Switch size="small" checked={p.requiereAprobacion === true} disabled={soloLectura || nivel !== 'editar'}
                    onChange={v => cambiarAprobacion(m.key, v)} aria-label={`Requiere aprobación en ${m.label}`} />
                  <span className="text-xs font-semibold text-gray-700">Requiere aprobación</span>
                  <Tooltip title="Sus cambios no se aplican solos: quedan en la cola de revisión para que otra persona los apruebe.">
                    <FiInfo size={13} className="text-gray-400 cursor-help" />
                  </Tooltip>
                </span>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default MatrizPermisos;
